import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, KeyRound, ShieldAlert, UserCog, Trash2, Archive, ArchiveRestore, LayoutGrid, FileText, UploadCloud, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminAccounts() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [providers, setProviders] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    const [a, p] = await Promise.all([
      api.get("/users/admins"),
      api.get(`/users/providers${showArchived ? "?include_archived=1" : ""}`),
    ]);
    setAdmins(a.data); setProviders(p.data);
  };
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleArchive = async (p) => {
    const action = p.is_archived ? "unarchive" : "archive";
    if (!window.confirm(`${action === "archive" ? "Archive" : "Restore"} ${p.name}?`)) return;
    try {
      await api.post(`/users/providers/${p.id}/${action}`);
      toast.success(action === "archive" ? "Provider archived" : "Provider restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setLayout = async (p, layout) => {
    try {
      await api.put(`/users/providers/${p.id}`, { ui_layout: layout });
      toast.success(`Layout set to ${layout === "detailed_narrative" ? "Detailed narrative" : "Compact checkbox"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeAdmin = async (id) => {
    if (!window.confirm("Remove this admin account?")) return;
    try { await api.delete(`/users/admins/${id}`); load(); toast.success("Admin removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitPw = async () => {
    if (!newPw || newPw.length < 6) return toast.error("Password must be ≥ 6 chars");
    try {
      await api.post(`/users/${pwTarget.id}/password`, { new_password: newPw });
      toast.success("Password updated");
      setPwTarget(null); setNewPw("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-accounts">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-label">Security</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Manage Admin Accounts</h1>
          <p className="text-sm text-muted-foreground">Provision additional admin users and manage all account passwords.</p>
        </div>
        <AddAdminDialog onCreated={load} />
      </div>

      <Card>
        <div className="p-4 border-b border-border flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-primary" /><p className="text-label">Admin accounts ({admins.length})</p></div>
        <div className="divide-y divide-border">
          {admins.map((a) => (
            <div key={a.id} className="p-4 flex items-center justify-between gap-3" data-testid={`admin-row-${a.id}`}>
              <div>
                <p className="font-semibold">{a.name} {a.id === user.id && <span className="text-xs text-muted-foreground">(you)</span>}</p>
                <p className="text-xs text-muted-foreground">{a.email}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPwTarget(a)} data-testid={`change-pw-admin-${a.id}`}>
                  <KeyRound className="w-4 h-4 mr-1" /> Change password
                </Button>
                <Button variant="outline" size="sm" disabled={a.id === user.id} onClick={() => removeAdmin(a.id)} className="text-red-600" data-testid={`remove-admin-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2"><UserCog className="w-4 h-4 text-primary" /><p className="text-label">Providers</p></div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="show-archived-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="cursor-pointer" />
            Show archived
          </label>
        </div>
        <div className="divide-y divide-border">
          {providers.map((p) => {
            const layout = p.ui_layout || "compact_checkbox";
            return (
              <div key={p.id} className={`p-4 flex items-start justify-between gap-3 flex-wrap ${p.is_archived ? "opacity-60" : ""}`} data-testid={`provider-row-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold flex items-center gap-2 flex-wrap">
                    {p.name}
                    {p.is_archived && <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">Archived</span>}
                    {p.revoked && <span className="text-xs text-red-600">(revoked)</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">{p.email}</p>
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`layout-toggle-${p.id}`}>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Daily Note layout:</span>
                    <Button
                      size="sm" variant={layout === "compact_checkbox" ? "default" : "outline"}
                      className="h-7 text-[11px] px-2"
                      onClick={() => setLayout(p, "compact_checkbox")}
                      data-testid={`set-compact-${p.id}`}
                    >
                      <LayoutGrid className="w-3 h-3 mr-1" /> Compact
                    </Button>
                    <Button
                      size="sm" variant={layout === "detailed_narrative" ? "default" : "outline"}
                      className="h-7 text-[11px] px-2"
                      onClick={() => setLayout(p, "detailed_narrative")}
                      data-testid={`set-detailed-${p.id}`}
                    >
                      <FileText className="w-3 h-3 mr-1" /> Detailed narrative
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => setPwTarget(p)} data-testid={`change-pw-provider-${p.id}`}>
                    <KeyRound className="w-4 h-4 mr-1" /> Password
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => toggleArchive(p)} data-testid={`archive-provider-${p.id}`}>
                    {p.is_archived ? <><ArchiveRestore className="w-4 h-4 mr-1" /> Restore</> : <><Archive className="w-4 h-4 mr-1" /> Archive</>}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <BulkGoalSeederCard />

      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && (setPwTarget(null), setNewPw(""))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Set a new password for {pwTarget?.name}.</DialogDescription>
          </DialogHeader>
          <Input type="password" placeholder="New password (min 6 chars)" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwTarget(null); setNewPw(""); }}>Cancel</Button>
            <Button onClick={submitPw} data-testid="submit-new-password">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddAdminDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const submit = async () => {
    try {
      await api.post("/users/admins", f);
      setF({ name: "", email: "", password: "" });
      setOpen(false); onCreated?.();
      toast.success("Admin created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="open-add-admin"><Plus className="w-4 h-4 mr-2" /> Add Admin</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Admin</DialogTitle>
          <DialogDescription>Creates a second/third admin user.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-admin-name" />
        <Input placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-admin-email" />
        <Input placeholder="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-admin-password" />
        <DialogFooter>
          <Button onClick={submit} data-testid="submit-new-admin">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkGoalSeederCard() {
  const [payload, setPayload] = useState("");
  const [createMissing, setCreateMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const sample = `[Client PID: PID_REPLACE_ME]
Goal: Replace this title — describe what the client is working on
- Replace with a support prompt (e.g. Hygiene reminder)
- Replace with another support prompt (e.g. Activity prompt)

Goal: Another goal title for the same client
- Outing planning
- Money counting

[Client PID: ANOTHER_PID]
Goal: Daily living skills practice
- Cooking prompts
- Hygiene reminder`;

  const submit = async () => {
    if (!payload.trim()) return toast.error("Paste a goal payload first");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/bulk-seed-goals", {
        payload,
        create_missing: createMissing,
      });
      setResult(data);
      toast.success(`Seeded goals across ${data.processed} client block(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk seed failed");
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="bulk-seed-card">
      <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <UploadCloud className="w-4 h-4 text-primary" />
          <div>
            <p className="text-label">Bulk Goal Seeder</p>
            <p className="text-[11px] text-muted-foreground">Paste agency roster text blocks (or JSON) to seed PCSP goals on the matching clients.</p>
          </div>
        </div>
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => setPayload(sample)}
          data-testid="bulk-seed-sample"
        >
          <Sparkles className="w-3.5 h-3.5 mr-1" /> Insert sample
        </Button>
      </div>
      <div className="p-4 space-y-3">
        <Textarea
          rows={10}
          placeholder={"[Client PID: 061031588]\nGoal: Goal title here\n- Support sub-item 1\n- Support sub-item 2"}
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          className="font-mono text-xs"
          data-testid="bulk-seed-payload"
        />
        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={createMissing}
              onChange={(e) => setCreateMissing(e.target.checked)}
              data-testid="bulk-seed-create-missing"
            />
            Create stub client docs for unknown PIDs
          </label>
          <Button
            onClick={submit}
            disabled={busy || !payload.trim()}
            data-testid="bulk-seed-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-1.5" />}
            {busy ? "Seeding…" : "Seed goals"}
          </Button>
        </div>
        {result && (
          <div className="mt-2 rounded-md border-2 border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="bulk-seed-result">
            <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Processed {result.processed} client block(s)
            </p>
            <div className="space-y-1 max-h-48 overflow-auto">
              {(result.results || []).map((r, i) => (
                <div key={i} className="text-[11px] font-mono flex justify-between gap-3 py-0.5 border-b border-emerald-100 last:border-0" data-testid={`bulk-seed-row-${i}`}>
                  <span className="truncate">
                    <span className="font-semibold">{r.pid}</span>{r.name ? ` · ${r.name}` : ""}
                  </span>
                  <span className={`shrink-0 ${r.status === "skipped" ? "text-amber-700" : "text-emerald-800"}`}>
                    {r.status === "skipped"
                      ? `skipped (${r.reason})`
                      : `+${r.added || 0} new · ~${r.updated || 0} updated`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

